import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CookieConsent, CONSENT_DEFAULT_SCRIPT } from "@/components/site/CookieConsent";
import { MotionConfig } from "framer-motion";

import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useMemo, type ReactNode } from "react";
import { I18nextProvider } from "react-i18next";

import appCss from "../styles.css?url";
import { OG_CORPORATE_IMAGE } from "@/lib/og";

import { reportLovableError } from "../lib/lovable-error-reporting";
import { ConciergeMount } from "@/components/site/ConciergeMount";
import { AuthSessionWatcher } from "@/components/site/AuthSessionWatcher";
import { Toaster } from "@/components/ui/sonner";

import { PreferenceSync } from "@/components/site/PreferenceSync";
import { BottomNav } from "@/components/site/BottomNav";
import { useBfcacheRestore } from "@/hooks/use-page-restore";
import logoAsset from "@/assets/mostrando-mexico-logo.webp.asset.json";
import i18nInstance, { type AppLang, SUPPORTED_LANGS, setLanguage } from "@/i18n";
import { resolveActiveLangAsync } from "@/lib/request-lang";
import { useCurrencyBootstrap } from "@/lib/currency";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const LOCALE_TAGS: Record<AppLang, string> = {
  es: "es_MX",
  en: "en_US",
  fr: "fr_FR",
  pt: "pt_BR",
  it: "it_IT",
  de: "de_DE",
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Runs on server AND client. Resolves the active language from the request
  // (cookie / Accept-Language) on the server or from localStorage on the
  // client, syncs react-i18next, and exposes it via router context so every
  // loader and component sees a single source of truth.
  beforeLoad: async ({ location }) => {
    const urlLang = location.pathname.split("/")[1]?.toLowerCase();
    const lang = urlLang && (SUPPORTED_LANGS as readonly string[]).includes(urlLang)
      ? urlLang as AppLang
      : await resolveActiveLangAsync();
    if (i18nInstance.language !== lang) {
      try {
        await setLanguage(lang);
      } catch {
        /* non-fatal — fall back to default resources */
      }
    }
    return { lang: lang as AppLang };
  },
  head: (ctx) => {
    const lang =
      (ctx as { match?: { context?: { lang?: AppLang } } }).match?.context?.lang ?? "es";
    return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Mostrando México" },
      { property: "og:locale", content: LOCALE_TAGS[lang] },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#FF5A00" },
      { title: "Mostrando México — Tours y experiencias auténticas" },
      { property: "og:title", content: "Mostrando México | Tours y Excursiones en Cancún, Riviera Maya" },
      { name: "twitter:title", content: "Mostrando México | Tours y Excursiones en Cancún, Riviera Maya" },
      { name: "description", content: "Reserva tours, excursiones y experiencias en Cancún, Riviera Maya, Tulum, Chichén Itzá, Holbox y Bacalar con reserva directa con el operador." },
      { property: "og:description", content: "Reserva tours, excursiones y experiencias en Cancún, Riviera Maya, Tulum, Chichén Itzá, Holbox y Bacalar con reserva directa con el operador." },
      { name: "twitter:description", content: "Reserva tours, excursiones y experiencias en Cancún, Riviera Maya, Tulum, Chichén Itzá, Holbox y Bacalar con reserva directa con el operador." },
      // Imagen social corporativa por defecto (fotografía real autorizada,
      // 1200x630, absoluta y versionada). Las rutas la sobrescriben.
      { property: "og:image", content: OG_CORPORATE_IMAGE },
      { property: "og:image:secure_url", content: OG_CORPORATE_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: OG_CORPORATE_IMAGE },

    ],
    links: [
      // Identidad corporativa: el favicon y los iconos de app usan SIEMPRE el
      // símbolo circular de Mostrando México (nunca el personaje Maya).
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon.png?v=4" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png?v=4" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png?v=4" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48.png?v=4" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=4" },
      { rel: "manifest", href: "/manifest.webmanifest?v=4" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // CDN de imágenes de tarjetas (WebP redimensionado + caché de 1 año)
      { rel: "preconnect", href: "https://wsrv.nl", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://wsrv.nl" },
    ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// En el servidor, varias visitas en distintos idiomas se atienden a la vez y
// comparten la instancia global de i18n. Cada visita usa su propia copia fijada
// en su idioma (comparte las traducciones cargadas), así el HTML nunca mezcla
// idiomas ni difiere del navegador al hidratar.
function RequestI18n({ lang, children }: { lang: string; children: ReactNode }) {
  const instance = useMemo(
    () =>
      typeof window === "undefined"
        ? i18nInstance.cloneInstance({ lng: lang, initAsync: false })
        : i18nInstance,
    [lang],
  );
  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
}

function RootShell({ children }: { children: ReactNode }) {
  const { lang } = Route.useRouteContext();
  return (
    <html lang={lang ?? "es"}>
      <head>
        {/*
          Las hojas de estilo se declaran estáticamente aquí, NO en head().links.
          <HeadContent /> reconcilia sus etiquetas en cada navegación del router:
          al entrar/salir de una ruta puede quitar y volver a insertar el <link>
          de CSS, y si esa re-descarga tarda o falla (móvil, bfcache, red lenta)
          la página se queda sin estilos hasta un refresco manual. Al vivir fuera
          de HeadContent, el CSS se carga una sola vez y nunca se desmonta.
        */}
        <link rel="stylesheet" href={appCss} />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700;800&family=Inter:wght@400;450;500;600;700&display=swap"
        />
        <script dangerouslySetInnerHTML={{ __html: CONSENT_DEFAULT_SCRIPT }} />
        <HeadContent />
      </head>
      <body>
        <RequestI18n lang={lang ?? "es"}>{children}</RequestI18n>
        <Scripts />
      </body>
    </html>
  );
}


function RootComponent() {
  const { queryClient, lang } = Route.useRouteContext();

  useBfcacheRestore();
  useCurrencyBootstrap();
  // Remember the last public page so sign-in returns the visitor there.
  const authRouter = useRouter();
  useEffect(() => authRouter.subscribe("onResolved", ({ toLocation }) => {
    const p = toLocation.pathname;
    if (/^\/(auth|login|reset-password|review\/)/.test(p)) return;
    try { sessionStorage.setItem("mm_auth_return", toLocation.href); } catch { /* ignore */ }
  }), [authRouter]);
  // Global display currency. Re-keying the subtree guarantees every price on
  // the page re-renders in the newly selected currency, instantly.


  // If the client-side detected language differs from what i18n booted with
  // (shouldn't happen after the switcher, but be defensive), realign so no
  // component re-renders with stale strings after hydration.
  useEffect(() => {
    if (lang && i18nInstance.language !== lang) {
      void setLanguage(lang);
    }
  }, [lang]);

  return (
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user">
        {/* Reserve space at the bottom for the fixed BottomNav (mobile) and the
            floating DesktopDock (>=1024px). */}
        <div className="pb-[calc(64px+env(safe-area-inset-bottom,0px))] lg:pb-0">
          <Outlet />
        </div>
        <PreferenceSync />
        <AuthSessionWatcher />
        <Toaster position="top-center" />

        <ConciergeMount />
        
        <CookieConsent />
        <BottomNav />
      </MotionConfig>
    </QueryClientProvider>

  );
}
